import api, { ApiResponse } from './api';

export interface CompetitionChannel {
  id: number;
  competition_id: number;
  channel_name: string;
  resolution_name: string;
  channel_type: 'numbered' | 'team' | 'special';
  team_id: number | null;
  special_rule_name: string | null;
  team_name: string | null;
  xaccel_server_id: number;
  created_at: string;
}

export type CompScope = 'all' | 'regular' | 'special';
export type CompSkip = 'unreachable' | 'unknown-state' | 'conflict' | 'already-exists' | 'exists-on-server' | 'not-selected' | 'not-applicable' | 'server-removed';

/** One expected stream (create) or DB row (delete) of a competition on one server, with its real state. */
export interface CompPlanItem {
  serverId: number;
  name: string;
  resolutionName: string;
  type: 'numbered' | 'team' | 'special';
  legId?: number;
  ref?: string;
  realStatus: string;
  verdict: 'ok' | 'missing' | 'create' | 'exists-on-server' | 'conflict' | 'unknown';
  act: boolean;
  op?: 'create' | 'delete' | 'forget-gone';
  skipped?: CompSkip;
  alreadyDone?: boolean;
}

export interface CompPlanServer {
  serverId: number;
  serverName: string;
  type: string;
  reachable: boolean;
  error?: string;
  ageMs: number | null;
  selected: boolean;
  /** The server is no longer in the account (deleted): listed with its rows, never selectable. */
  removed?: boolean;
  items: CompPlanItem[];
}

export interface CompServerResult {
  serverId: number;
  serverName: string;
  reachable: boolean;
  error?: string;
  done: Array<{ name: string; legId?: number; alreadyGone?: boolean }>;
  skipped: Array<{ name: string; reason: CompSkip }>;
  failed: Array<{ name: string; legId?: number; error: string }>;
}

export interface ProvisionResult {
  created: number;
  failed: number;
  channels: CompetitionChannel[];
  errors?: string[];
  results?: CompServerResult[];
}

/** Real reads of several servers can take a while (8 s budget per server, in parallel). */
const PLAN_TIMEOUT_MS = 30_000;
const RUN_TIMEOUT_MS = 180_000;

export const channelsService = {
  async list(competitionId: number): Promise<CompetitionChannel[]> {
    const response = await api.get<ApiResponse<CompetitionChannel[]>>(
      `/competitions/${competitionId}/channels`
    );
    return response.data.data;
  },

  async listByType(competitionId: number, type: string): Promise<CompetitionChannel[]> {
    const response = await api.get<ApiResponse<CompetitionChannel[]>>(
      `/competitions/${competitionId}/channels`, { params: { type } }
    );
    return response.data.data;
  },

  /** Dry run: the real state per server of what create / delete would touch. Never writes. */
  /** `fresh: true` (the Refresh button): a fresh read that also re-probes servers whose circuit is open. */
  async plan(competitionId: number, action: 'create' | 'delete', body: { servers: number[]; scope?: CompScope; legIds?: number[]; fresh?: boolean }): Promise<CompPlanServer[]> {
    const response = await api.post<ApiResponse<{ servers: CompPlanServer[] }>>(`/competitions/${competitionId}/channels/plan`, { action, ...body }, { timeout: PLAN_TIMEOUT_MS });
    return response.data.data.servers;
  },

  /** Creates the missing channels on exactly these servers (after the preview). */
  async provision(competitionId: number, servers: number[]): Promise<ProvisionResult> {
    const response = await api.post<ApiResponse<ProvisionResult>>(`/competitions/${competitionId}/channels/provision`, { servers, confirm: true }, { timeout: RUN_TIMEOUT_MS });
    return response.data.data;
  },

  async provisionSpecial(competitionId: number, servers: number[]): Promise<ProvisionResult> {
    const response = await api.post<ApiResponse<ProvisionResult>>(`/competitions/${competitionId}/channels/provision-special`, { servers, confirm: true }, { timeout: RUN_TIMEOUT_MS });
    return response.data.data;
  },

  /** Deletes exactly these rows (by id; the server re-reads the real state; failures keep their row). */
  async deleteByIds(competitionId: number, legIds: number[]): Promise<{ deleted: number; failed: number; results: CompServerResult[] }> {
    const response = await api.post<ApiResponse<{ deleted: number; failed: number; results: CompServerResult[] }>>(`/competitions/${competitionId}/channels/delete`, { legIds, confirm: true }, { timeout: RUN_TIMEOUT_MS });
    return response.data.data;
  },

  async deleteAllDbOnly(competitionId: number, serverId?: number): Promise<void> {
    await api.delete(`/competitions/${competitionId}/channels/db-only`, { params: { confirm: true, ...(serverId ? { serverId } : { all: true }) } });
  },

  async deleteSpecialDbOnly(competitionId: number): Promise<void> {
    await api.delete(`/competitions/${competitionId}/channels/special/db-only`, { params: { confirm: true } });
  },

  async deleteSingleDbOnly(competitionId: number, channelId: number): Promise<void> {
    await api.delete(`/competitions/${competitionId}/channels/${channelId}/db-only`, { params: { confirm: true } });
  },

  async deleteSingle(competitionId: number, channelId: number): Promise<void> {
    await api.delete(`/competitions/${competitionId}/channels/${channelId}`, { params: { confirm: true }, timeout: RUN_TIMEOUT_MS });
  },

  async pushConfig(competitionId: number): Promise<{ updated: number; failed?: number; skipped?: number; total: number; failures?: Array<{ channelName: string; serverId: number; error: string }> }> {
    const response = await api.post<ApiResponse<{ updated: number; failed?: number; skipped?: number; total: number; failures?: Array<{ channelName: string; serverId: number; error: string }> }>>(
      `/competitions/${competitionId}/channels/push-config`
    );
    return response.data.data;
  },

  async syncFromServer(competitionId: number): Promise<{ created: number; total: number }> {
    const response = await api.post<ApiResponse<{ created: number; total: number }>>(
      `/competitions/${competitionId}/channels/sync`
    );
    return response.data.data;
  },
};
